import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { Globe, Palette, TrendingUp, Share2, ShoppingCart, Smartphone, CheckCircle2, RefreshCw } from "lucide-react";
import { Link } from "wouter";
import { SocialPricingSection } from "@/components/SocialPricingSection";

export default function Servicios() {
  const { tr } = useLang();
  const [isAnnual, setIsAnnual] = useState(true);

  const services = [
    {
      id: "web",
      icon: Globe,
      title: tr.services.web,
      desc: tr.servicesPage.webFullDesc,
      features: tr.servicesPage.webFeatures,
    },
    {
      id: "design",
      icon: Palette,
      title: tr.services.design,
      desc: tr.servicesPage.designFullDesc,
      features: tr.servicesPage.designFeatures,
    },
    {
      id: "marketing",
      icon: TrendingUp,
      title: tr.services.marketing,
      desc: tr.servicesPage.marketingFullDesc,
      features: tr.servicesPage.marketingFeatures,
    },
    {
      id: "social",
      icon: Share2,
      title: tr.services.social,
      desc: tr.servicesPage.socialFullDesc,
      features: tr.servicesPage.socialFeatures,
    },
    {
      id: "ecommerce",
      icon: ShoppingCart,
      title: tr.services.ecommerce,
      desc: tr.servicesPage.ecommerceFullDesc,
      features: tr.servicesPage.ecommerceFeatures,
    },
    {
      id: "apps",
      icon: Smartphone,
      title: tr.services.apps,
      desc: tr.servicesPage.appsFullDesc,
      features: tr.servicesPage.appsFeatures,
    },
  ];

  return (
    <div className="bg-background text-foreground overflow-x-hidden selection:bg-primary/30">

      {/* Hero (Dark + bg image) */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/assets/project-1.png" alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-[#0a0f1e]/88" />
          <div className="absolute inset-0 dark-texture" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-8 text-white">
              {tr.servicesPage.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto">
              {tr.servicesPage.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Details (alternating Light/Dark) */}
      {services.map((svc, i) => (
        <section
          key={svc.id}
          className={`py-24 px-6 relative overflow-hidden ${
            i % 2 === 0
              ? 'bg-slate-50 text-slate-900 border-y border-slate-200'
              : 'text-foreground dark-texture'
          }`}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center relative z-10">
            <motion.div
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={i % 2 !== 0 ? 'md:order-2' : ''}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-8 ${i % 2 === 0 ? 'bg-primary/10' : 'bg-primary/20'}`}>
                <svc.icon className="w-8 h-8 text-primary" />
              </div>
              <h2 className={`text-4xl font-extrabold mb-6 tracking-tight ${i % 2 === 0 ? 'text-slate-900' : 'text-white'}`}>{svc.title}</h2>
              <p className={`text-lg font-light leading-relaxed mb-8 ${i % 2 === 0 ? 'text-slate-600' : 'text-muted-foreground'}`}>{svc.desc}</p>
              <h4 className={`font-bold mb-4 uppercase tracking-wider text-sm ${i % 2 === 0 ? 'text-slate-500' : 'text-muted-foreground'}`}>{tr.servicesPage.whatsIncluded}</h4>
              <ul className="space-y-3">
                {svc.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                    <span className={i % 2 === 0 ? 'text-slate-700' : 'text-slate-300'}>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl ${i % 2 !== 0 ? 'md:order-1 border border-border' : 'border border-slate-200'}`}
            >
              <img src={`/assets/project-${(i) + 1}.png`} alt={svc.title} className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </section>
      ))}

      {/* Pricing Section (Light) */}
      <section className="py-32 px-6 bg-slate-50 border-y border-slate-200 text-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">{tr.pricing.title}</h2>
            <p className="text-slate-600 text-xl max-w-2xl mx-auto font-light mb-10">{tr.pricing.desc}</p>
            <div className="inline-flex items-center p-1 bg-slate-200 rounded-full">
              <button onClick={() => setIsAnnual(false)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${!isAnnual ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>{tr.pricing.monthly}</button>
              <button onClick={() => setIsAnnual(true)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${isAnnual ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
                {tr.pricing.annual}
                <span className="bg-primary/10 text-primary text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold">{tr.pricing.save}</span>
              </button>
            </div>
            {isAnnual && <p className="text-sm text-slate-500 mt-3">{tr.pricing.annualPaymentNote}</p>}
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-xl font-bold mb-2">{tr.pricing.onepage}</h3>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">${isAnnual ? '250' : '25'}</span>
                <span className="text-slate-500 font-medium">USD{isAnnual ? tr.pricing.year : tr.pricing.month}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {tr.pricing.onepageFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /><span className="text-slate-600 text-sm">{f}</span></li>
                ))}
              </ul>
              {isAnnual && <div className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-5 text-slate-500"><RefreshCw className="w-3.5 h-3.5 shrink-0" /><span>{tr.pricing.renewalNote}</span></div>}
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-semibold h-12 w-full bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors">{tr.nav.startProject}</Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-slate-900 rounded-3xl p-8 border-2 border-primary shadow-xl flex flex-col relative text-white transform md:-translate-y-4">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full">{tr.pricing.popular}</div>
              <h3 className="text-xl font-bold mb-1">{tr.pricing.multipages}</h3>
              <p className="text-slate-400 text-sm mb-2">{tr.pricing.multipagesDesc}</p>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">${isAnnual ? '400' : '40'}</span>
                <span className="text-slate-400 font-medium">USD{isAnnual ? tr.pricing.year : tr.pricing.month}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {tr.pricing.multipagesFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /><span className="text-slate-300 text-sm">{f}</span></li>
                ))}
              </ul>
              {isAnnual && <div className="flex items-center gap-2 text-xs bg-white/10 rounded-xl px-3 py-2 mb-5 text-slate-300"><RefreshCw className="w-3.5 h-3.5 shrink-0" /><span>{tr.pricing.renewalNote}</span></div>}
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-bold h-12 w-full bg-primary text-white hover:bg-primary/90 transition-colors shadow-[0_0_20px_rgba(56,189,248,0.3)]">{tr.nav.startProject}</Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-xl font-bold mb-2">{tr.pricing.custom}</h3>
              <p className="text-slate-500 text-sm mb-2">{tr.pricing.customDesc}</p>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-slate-800">{tr.pricing.customPrice}</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {tr.pricing.customFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" /><span className="text-slate-600 text-sm">{f}</span></li>
                ))}
              </ul>
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-semibold h-12 w-full bg-slate-900 text-white hover:bg-slate-800 transition-colors">{tr.pricing.contactQuote}</Link>
            </motion.div>
          </div>
        </div>
      </section>

      <SocialPricingSection />
    </div>
  );
}
